// Which ONDC transit product lines the backend has live. Fetched once and used
// to hide product lines the backend has turned off, so users never tap into a
// 503. Public endpoint (App Check-exempt); the shared authed core still attaches
// headers, which the backend simply ignores.
import { queryClient } from '@/lib/platform';
import { request } from './http';
import { capabilityKeys } from './query-keys';

export interface TransitCapabilities {
  metro: boolean;
  bus: boolean;
  rides: boolean;
  passes: boolean;
}

/**
 * Fail-closed default: hide every product line until the server confirms it's
 * live (opt-in model — the backend defaults every line off). A line shows only
 * on a positive response, so a stale/failed fetch never surfaces a disabled line.
 */
export const NONE_ENABLED: TransitCapabilities = {
  metro: false,
  bus: false,
  rides: false,
  passes: false,
};

export const capabilitiesApi = {
  get: () => request<TransitCapabilities>('GET', '/api/v1/transit/capabilities'),
};

const CAPABILITIES_STALE_MS = 5 * 60_000;

/**
 * Resolve capabilities for use outside React (route `beforeLoad` guards),
 * reusing the React Query cache. Fails closed — returns NONE_ENABLED if the
 * fetch throws — so a blip redirects to the launcher rather than opening a line
 * that may be off.
 */
export async function ensureCapabilities(): Promise<TransitCapabilities> {
  try {
    return await queryClient.ensureQueryData({
      queryKey: capabilityKeys.all(),
      queryFn: capabilitiesApi.get,
      staleTime: CAPABILITIES_STALE_MS,
    });
  } catch {
    return NONE_ENABLED;
  }
}
